// Review page: an external AI asked to change something as this person.
//
// ⚠️ This click is the ONLY thing that lets the change happen. The AI's own
// "the user confirmed" proves nothing, so it waits here until the person —
// signed in, and nobody else — approves exactly what is shown.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bot, CheckCircle2, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { getApiErrorMessage } from '@/lib/apiError';
import { decideAiActionRequest, getAiActionRequest, type AiActionRequest } from '@/services/developerService';

/** Argument names shown to a person, not a developer. */
function label(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function display(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export const AiApprovalPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { id = '' } = useParams();
  const [req, setReq] = useState<AiActionRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'approve' | 'decline' | null>(null);

  useEffect(() => {
    getAiActionRequest(id)
      .then(setReq)
      .catch((e) => setError(getApiErrorMessage(e, t('ai_approval.not_found', {
        defaultValue: 'This request was not found. It may belong to someone else or have been removed.' }))));
  }, [id, t]);

  const decide = async (approve: boolean) => {
    setBusy(approve ? 'approve' : 'decline');
    setError(null);
    try {
      setReq(await decideAiActionRequest(id, approve));
    } catch (e) {
      setError(getApiErrorMessage(e, t('ai_approval.failed', { defaultValue: 'Could not record your decision.' })));
    } finally {
      setBusy(null);
    }
  };

  if (error && !req) {
    return <p className="mx-auto max-w-xl p-6 text-sm text-danger" data-testid="ai-approval-error">{error}</p>;
  }
  if (!req) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const args = Object.entries(req.args || {}).filter(([k]) => k !== 'confirm' && k !== 'approvalRequestId');
  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bot className="h-5 w-5" aria-hidden />
            {t('ai_approval.title', { defaultValue: 'An AI assistant wants to act for you' })}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {t('ai_approval.intro', {
              defaultValue: 'Nothing happens unless you approve. Check every detail — this is exactly what will be done in your name.' })}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-base font-medium text-charcoal" data-testid="ai-approval-summary">{req.summary}</p>
          {args.length > 0 && (
            <dl className="divide-y divide-border rounded-md border border-border text-sm">
              {args.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 px-3 py-2">
                  <dt className="text-muted-foreground">{label(k)}</dt>
                  <dd className="text-right text-charcoal">{display(v)}</dd>
                </div>
              ))}
            </dl>
          )}

          {req.status === 'pending' && (
            <>
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                {t('ai_approval.expires', { defaultValue: 'This request expires at {{time}}.',
                  time: new Date(req.expiresAt).toLocaleTimeString() })}
              </p>
              {error && <p className="text-sm text-danger">{error}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => decide(false)} disabled={busy !== null}>
                  {busy === 'decline' && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {t('ai_approval.decline', { defaultValue: 'Decline' })}
                </Button>
                <Button onClick={() => decide(true)} disabled={busy !== null}>
                  {busy === 'approve' && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {t('ai_approval.approve', { defaultValue: 'Approve' })}
                </Button>
              </div>
            </>
          )}
          {req.status === 'approved' && (
            <p className="flex items-center gap-2 text-sm text-success" data-testid="ai-approval-approved">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              {t('ai_approval.approved', { defaultValue: 'Approved. Go back to the assistant — it can now do this once.' })}
            </p>
          )}
          {req.status === 'used' && (
            <p className="text-sm text-muted-foreground">
              {t('ai_approval.used', { defaultValue: 'Approved and already carried out.' })}
            </p>
          )}
          {req.status === 'declined' && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ai-approval-declined">
              <XCircle className="h-4 w-4" aria-hidden />
              {t('ai_approval.declined', { defaultValue: 'Declined. The assistant cannot do this.' })}
            </p>
          )}
          {req.status === 'expired' && (
            <p className="text-sm text-muted-foreground">
              {t('ai_approval.expired', { defaultValue: 'This request expired. Ask the assistant to prepare it again.' })}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AiApprovalPage;
